/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import { SealCheckIcon } from '@ahamove/icons/subscription';

import { formatNumber } from '@ahm/common-helpers';
import {
  Avatar,
  Box,
  Card,
  Group,
  Pill,
  Stack,
  Text,
  rem,
} from '@mantine/core';
import { useLocale, useTranslations } from 'next-intl';

import type { SubscriptionDetail, SubscriptionPlan } from '../api';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

type SubscriptionDetailContent = {
  details: SubscriptionDetail;
  plan: SubscriptionPlan;
  termAndConditionUrl: string;
};

export default function SubscriptionBenefit({
  details,
  plan,
}: SubscriptionDetailContent) {
  const tSubscription = useTranslations('subscription');

  const locale = useLocale();
  const isMobile = useIsMobile();

  if (!plan) return null;

  return (
    <Box pos="relative">
      <Card
        pos="relative"
        w="100%"
        h="100%"
        radius={rem(12)}
        style={{
          borderTop: '24px 24px 0 0',
          borderColor: 'transparent',
          boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
        }}
      >
        <Card.Section>
          <Group
            wrap="nowrap"
            p={rem(12)}
            px={isMobile ? rem(12) : rem(16)}
            justify={plan?.saving?.[locale] ? 'space-between' : 'center'}
            bg="linear-gradient(96deg, #FFF -41.27%, #FFD9BE -4.14%, #FFF 26.08%, #FFDBC1 60.14%, #FFEBDC 77.17%, #FFF 88.78%, #FFD0B2 113.55%)"
          >
            <Text
              size={isMobile ? 'sm' : 'md'}
              fw={600}
              lh={isMobile ? rem(20) : rem(24)}
              c="neutral.7"
            >
              {tSubscription('package_benefit')}
            </Text>
            {plan?.saving?.[locale] && (
              <Pill
                c="var(--mantine-color-green-6)"
                px={rem(12)}
                py={rem(4)}
                radius={rem(24)}
                bg="var(--mantine-color-green-1)"
                h="fit-content"
                w="fit-content"
                fw={500}
              >
                <Text fw={700} size={isMobile ? 'xs' : 'sm'}>
                  {plan?.saving?.[locale]}
                </Text>
              </Pill>
            )}
          </Group>
        </Card.Section>
        <Stack
          pt={isMobile ? rem(12) : rem(16)}
          pb={0}
          gap={isMobile ? rem(12) : rem(16)}
        >
          {plan.promo_code_samples.length > 0
            ? plan.promo_code_samples.map((item) => (
                <Group justify="space-between" key={item.id} wrap="nowrap">
                  <Group gap={isMobile ? rem(8) : rem(16)} wrap="nowrap">
                    <Avatar
                      color="white"
                      style={{ flexShrink: 0 }}
                      radius={rem(8)}
                      src={item.icon_url}
                      w={isMobile ? rem(24) : rem(32)}
                      h={isMobile ? rem(24) : rem(32)}
                      alt={item.id}
                      variant="transparent"
                      styles={{ image: { objectFit: 'contain' } }}
                    >
                      <SealCheckIcon
                        width={isMobile ? 24 : 32}
                        height={isMobile ? 24 : 32}
                      />
                    </Avatar>
                    <Text lineClamp={2} lh={rem(20)} fw={500} size="sm">
                      {item.title?.[locale]}
                    </Text>
                  </Group>

                  <Text
                    style={{ borderRadius: rem(8) }}
                    py={rem(8)}
                    px={rem(4)}
                    c="var(--mantine-color-green-6)"
                    bg="var(--mantine-color-green-1)"
                    h={rem(32)}
                    miw={rem(38)}
                    ta="center"
                    fw={700}
                    size={isMobile ? 'xs' : 'md'}
                    lh={isMobile ? rem(16) : rem(14)}
                  >
                    x{formatNumber(item.max_use)}
                  </Text>
                </Group>
              ))
            : null}
        </Stack>
      </Card>
      {details?.term_and_condition?.[locale] &&
      details?.term_and_condition_title?.[locale] ? (
        <>
          <Text
            mb={rem(0)}
            fw={500}
            size={isMobile ? 'xs' : 'sm'}
            py={isMobile ? rem(12) : rem(16)}
          >
            {details?.term_and_condition?.[locale]} {''}
            <Text
              style={{ cursor: 'pointer' }}
              component="a"
              href={details?.term_and_condition_url}
              target="_blank"
              c="var(--mantine-color-blue-4)"
              fw={500}
            >
              {details?.term_and_condition_title?.[locale]}
            </Text>
          </Text>
        </>
      ) : (
        <Text
          mb={rem(0)}
          fw={500}
          size={isMobile ? 'xs' : 'sm'}
          py={isMobile ? rem(12) : rem(16)}
        >
          {tSubscription.rich('term_and_condition', {
            span: (chunks) => (
              <Text
                style={{ cursor: 'pointer' }}
                component="a"
                href={details.term_and_condition_url}
                target="_blank"
                c="var(--mantine-color-blue-4)"
              >
                {chunks}
              </Text>
            ),
          })}
        </Text>
      )}
    </Box>
  );
}
